import { ItemCell } from '../../components/table-cells';
import { useItemPictures } from '../../hooks/useItemPictures';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Descriptions,
  Table,
  Tag,
  Button,
  Space,
  Typography,
  Card,
  Spin,
  Row,
  Col,
  Divider,
  Modal,
  InputNumber,
  Input,
  Select,
  message,
  Timeline,
  Tooltip,
  Alert,
  Statistic,
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined,
  RollbackOutlined,
  SendOutlined,
  DollarOutlined,
  DeleteOutlined,
  PrinterOutlined,
  PaperClipOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { getGRN, createGRNReturn, updateGRNReturnStatus, deleteGRN, getReceiptProofLink } from '../../api';
import { money, OUTCOME_LABEL, PATH_LABEL, PAYMENT_LABEL } from '../../components/receiving/shared';
import { buildGrnSlipHtml } from '../../utils/grnSlip';
import { useAuth } from '../../context/AuthContext';
import EntityHistory from '../../components/EntityHistory';
import ResponsiveTable from '../../components/responsive/ResponsiveTable';

const { Title, Text } = Typography;

const INSPECTION_COLORS: Record<string, string> = {
  Discount: 'gold', // GRN phase 1: rejected goods kept at a discount
  Passed: 'green',
  Partial: 'orange',
  Failed: 'red',
};

const RETURN_STATUS_COLORS: Record<string, string> = {
  Pending: 'orange',
  Shipped: 'blue',
  Credited: 'green',
  Refunded: 'cyan',
};

const GRNView: React.FC = () => {
  const { grnId } = useParams<{ grnId: string }>();
  const navigate = useNavigate();
  const { isProcurement, isAdmin, user, canAccess } = useAuth();
  const [loading, setLoading] = useState(true);
  const [grn, setGrn] = useState<any>(null);
  const [returnModalOpen, setReturnModalOpen] = useState(false);
  const [returningLine, setReturningLine] = useState<any>(null);
  const [returnForm, setReturnForm] = useState({ returnQty: 0, returnReason: '', notes: '' });
  const [returnLoading, setReturnLoading] = useState(false);
  const [settlementModalOpen, setSettlementModalOpen] = useState(false);
  const [settlingReturn, setSettlingReturn] = useState<any>(null);
  const [settlementForm, setSettlementForm] = useState({ type: 'Credit' as 'Credit' | 'Refund', creditNoteRef: '', refundRef: '', refundAmount: 0 });
  // Item pictures (2026-09-22): the receiver checks the delivery against the picture on the line.
  // Declared here because this page returns early while it loads.
  const lineItemIds = useMemo(
    () => (grn?.lines || []).map((l: any) => l.poLine?.mrLine?.catalogItem?.id ?? null),
    [grn]
  );
  const { pictures: linePictures, reload: reloadLinePicture } = useItemPictures(lineItemIds);
  const fetchGRN = useCallback(async () => {
    if (!grnId) return;
    setLoading(true);
    try {
      const data = await getGRN(grnId);
      setGrn(data);
    } catch (err) {
      message.error('Failed to load GRN');
    } finally {
      setLoading(false);
    }
  }, [grnId]);

  useEffect(() => {
    fetchGRN();
  }, [fetchGRN]);

  const fmtNum = (val: any) =>
    val != null ? Number(val).toLocaleString('en-US', { minimumFractionDigits: 2 }) : '-';

  const openReturnModal = (line: any) => {
    const existingReturns = (line.returns || []).reduce((s: number, r: any) => s + Number(r.returnQty), 0);
    const maxReturn = Number(line.rejectedQty) - existingReturns;
    setReturningLine({ ...line, maxReturn });
    setReturnForm({ returnQty: maxReturn, returnReason: '', notes: '' });
    setReturnModalOpen(true);
  };

  const handleCreateReturn = async () => {
    if (!returningLine) return;
    if (returnForm.returnQty <= 0) { message.warning('Enter return quantity'); return; }
    if (!returnForm.returnReason.trim()) { message.warning('Enter return reason'); return; }
    setReturnLoading(true);
    try {
      await createGRNReturn(returningLine.id, {
        returnQty: returnForm.returnQty,
        returnReason: returnForm.returnReason,
        notes: returnForm.notes || undefined,
      });
      message.success('Return created successfully');
      setReturnModalOpen(false);
      fetchGRN();
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to create return');
    } finally {
      setReturnLoading(false);
    }
  };

  const handleReturnStatusChange = async (returnId: number, status: string) => {
    try {
      await updateGRNReturnStatus(returnId, { status });
      message.success(`Return status updated to ${status}`);
      fetchGRN();
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to update return');
    }
  };

  const openSettlementModal = (record: any) => {
    setSettlingReturn(record);
    setSettlementForm({ type: 'Credit', creditNoteRef: '', refundRef: '', refundAmount: 0 });
    setSettlementModalOpen(true);
  };

  const handleSettlement = async () => {
    if (!settlingReturn) return;
    try {
      const status = settlementForm.type === 'Credit' ? 'Credited' : 'Refunded';
      const payload: any = {
        status,
        settlementType: settlementForm.type,
      };
      if (settlementForm.type === 'Credit') {
        payload.creditNoteRef = settlementForm.creditNoteRef || undefined;
      } else {
        payload.refundRef = settlementForm.refundRef || undefined;
        if (settlementForm.refundAmount > 0) payload.refundAmount = settlementForm.refundAmount;
      }
      await updateGRNReturnStatus(settlingReturn.id, payload);
      message.success(`Return settled as ${settlementForm.type === 'Credit' ? 'Credit Note' : 'Refund'}`);
      setSettlementModalOpen(false);
      fetchGRN();
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to settle return');
    }
  };

  const handleDeleteGRN = () => {
    Modal.confirm({
      title: 'Delete GRN?',
      content: `This will permanently delete ${grnId} and reverse all received quantities on the PO. This cannot be undone.`,
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await deleteGRN(grnId!);
          message.success('GRN deleted');
          navigate('/grns');
        } catch (err: any) {
          message.error(err.response?.data?.error || 'Failed to delete GRN');
        }
      },
    });
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>;
  if (!grn) return <div>GRN not found</div>;

  const lines = grn.lines || [];
  const totalDelivered = lines.reduce((s: number, l: any) => s + Number(l.deliveredQty), 0);
  const totalAccepted = lines.reduce((s: number, l: any) => s + Number(l.acceptedQty), 0);
  const totalRejected = lines.reduce((s: number, l: any) => s + Number(l.rejectedQty), 0);
  const qualityPct = totalDelivered > 0 ? Math.round((totalAccepted / totalDelivered) * 100) : 100;
  const hasRejections = totalRejected > 0;
  // GRN phase 1 (2026-10-21): totals per unit and in money, line value and "Goes to".
  const priceOf = (l: any) => Number(l.poLine?.actualUnitPrice ?? l.poLine?.unitPrice ?? 0);
  const perUnit = (field: 'acceptedQty' | 'rejectedQty') => {
    const m = new Map<string, number>();
    lines.forEach((l: any) => { const v = Number(l[field] || 0); if (v) m.set(l.poLine?.unit?.name || '', (m.get(l.poLine?.unit?.name || '') || 0) + v); });
    return Array.from(m.entries()).map(([u, v]) => `${v.toLocaleString('en-US', { maximumFractionDigits: 2 })} ${u}`.trim()).join(' · ') || '0';
  };
  const receiptValue = lines.reduce((s: number, l: any) => s + Number(l.acceptedQty) * priceOf(l), 0);
  const proofFiles: any[] = Array.isArray(grn.proofFiles) ? grn.proofFiles : [];
  const openProof = async (i: number) => {
    try { window.open(await getReceiptProofLink(grn.grnId, i), '_blank', 'noopener'); } catch (e: any) { message.error(e?.response?.data?.error || 'Could not open the file'); }
  };
  const outcomeText = (l: any) => {
    if (!l.rejectOutcome) return null;
    const bits = [OUTCOME_LABEL[l.rejectOutcome] || l.rejectOutcome];
    if (l.rejectOutcomeRef) bits.push(l.rejectOutcomeRef);
    if (l.rejectOutcomeDue) bits.push(`due ${dayjs(l.rejectOutcomeDue).format('D MMM')}`);
    if (l.rejectOutcomeAmount != null) bits.push(`${money(Number(l.rejectOutcomeAmount))} off`);
    if (l.rejectOutcome === 'writeoff') {
      const cs = l.poLine?.cancelStatus;
      bits.push(cs === 'Pending' ? 'waiting for Ahmed (Cancellations)' : cs === 'Approved' ? 'approved by Ahmed' : cs === 'Rejected' ? 'not approved by Ahmed' : 'sent to Ahmed');
    }
    return bits.join(' · ');
  };
  const printSlip = () => {
    const html = buildGrnSlipHtml({
      grnId: grn.grnId, poId: grn.poHeader?.poId, path: PATH_LABEL[grn.poHeader?.purchasePath] || null,
      vendor: grn.poHeader?.vendor?.name || '', department: grn.poHeader?.department?.name, receivedOn: grn.receivedOn,
      receivedBy: grn.receivedBy?.displayName, paidBy: grn.poHeader?.paymentMethod ? PAYMENT_LABEL[grn.poHeader.paymentMethod] : null,
      deliveryNote: grn.deliveryNoteRef, invoice: grn.supplierInvoice, notes: grn.notes,
      lines: lines.map((l: any) => ({
        no: l.poLine?.lineNo, code: l.poLine?.mrLine?.catalogItem?.partNumber || l.poLine?.partNumber, item: l.poLine?.itemDescription || '',
        goesTo: l.poLine?.costCenter ? `${l.poLine.costCenter.code}${l.poLine.costCenter.name ? ` ${l.poLine.costCenter.name}` : ''}` : null,
        accepted: Number(l.acceptedQty), rejected: Number(l.rejectedQty), unit: l.poLine?.unit?.name, price: priceOf(l), outcome: outcomeText(l),
      })),
    }, { printedAt: dayjs().format('DD MMM YYYY [at] HH:mm'), printedBy: user?.displayName || '' });
    const w = window.open('', '_blank');
    if (!w) { message.error('Allow pop-ups for this site to print'); return; }
    w.document.write(html);
    w.document.close();
    setTimeout(() => w.print(), 300);
  };
  const canManage = canAccess('purchasing.grns', 'full');

  const lineColumns: any[] = [
    {
      title: 'PO Line #',
      key: 'poLineNo',
      width: 90,
      align: 'center' as const,
      render: (_: any, record: any) => record.poLine?.lineNo || '-',
    },
    {
      title: 'Description',
      key: 'description',
      width: 200,
      ellipsis: true,
      render: (_: any, record: any) => record.poLine?.itemDescription || '-',
    },
    {
      title: 'Source MR',
      key: 'sourceMR',
      width: 140,
      render: (_: any, record: any) => {
        const mrHeader = record.poLine?.mrLine?.mrHeader;
        if (!mrHeader) return <span style={{ color: '#999' }}>—</span>;
        return (
          <div>
            <a onClick={() => navigate(`/mrs/${mrHeader.id}`)} style={{ cursor: 'pointer' }}>{mrHeader.mrId}</a>
            <br />
            <Tag style={{ fontSize: 11, marginTop: 2 }}>{mrHeader.department?.code}</Tag>
          </div>
        );
      },
    },
    {
      title: 'Part #',
      // 200 + 46 for the 36px picture thumbnail that now sits before the code (2026-09-22).
      key: 'partNumber',
      width: 246,
      render: (_: any, record: any) => {
        const ci = record.poLine?.mrLine?.catalogItem;
        const pn = ci?.partNumber || record.poLine?.partNumber;
        if (!pn) return <Text type="secondary">-</Text>;
        return (
          <ItemCell
            partNumber={pn}
            linkTo={ci?.id ? `/catalog/${ci.id}` : undefined}
            compact
            showThumb
            pictureUrl={ci?.id ? linePictures[ci.id]?.url : null}
            thumbItemId={ci?.id ?? null}
            onThumbError={ci?.id ? () => reloadLinePicture(ci.id) : undefined}
          />
        );
      },
    },
    {
      title: 'Delivered',
      dataIndex: 'deliveredQty',
      width: 90,
      align: 'right' as const,
      render: (val: any) => Number(val),
    },
    {
      title: 'Accepted',
      dataIndex: 'acceptedQty',
      width: 90,
      align: 'right' as const,
      render: (val: any) => <Text type="success">{Number(val)}</Text>,
    },
    {
      title: 'Price',
      key: 'price',
      width: 100,
      align: 'right' as const,
      render: (_: any, record: any) => money(priceOf(record)),
    },
    {
      title: 'Value',
      key: 'value',
      width: 110,
      align: 'right' as const,
      render: (_: any, record: any) => money(Number(record.acceptedQty) * priceOf(record)),
    },
    {
      title: 'Goes to',
      key: 'goesTo',
      width: 160,
      render: (_: any, record: any) => record.poLine?.costCenter
        ? <span><code>{record.poLine.costCenter.code}</code> {record.poLine.costCenter.name}</span>
        : <Text type="secondary">—</Text>,
    },
    {
      title: 'Rejected',
      dataIndex: 'rejectedQty',
      width: 90,
      align: 'right' as const,
      render: (val: any) => {
        const v = Number(val);
        return v > 0 ? <Text type="danger">{v}</Text> : <Text type="secondary">0</Text>;
      },
    },
    {
      title: 'Inspection',
      dataIndex: 'inspectionStatus',
      width: 100,
      align: 'center' as const,
      render: (val: string) => (
        <Tag color={INSPECTION_COLORS[val] || 'default'}>{val}</Tag>
      ),
    },
    {
      title: 'Reject Reason',
      dataIndex: 'rejectReason',
      width: 120,
      render: (val: string) => val ? (
        <Tag color="red">{val.replace(/_/g, ' ')}</Tag>
      ) : <Text type="secondary">-</Text>,
    },
    {
      title: 'What happens next',
      key: 'outcome',
      width: 200,
      render: (_: any, record: any) => outcomeText(record) || <Text type="secondary">-</Text>,
    },
    {
      title: 'Inspection Notes',
      dataIndex: 'inspectionNotes',
      width: 180,
      ellipsis: true,
      render: (val: string) => val || <Text type="secondary">-</Text>,
    },
  ];

  // Add return action column if user can manage and there are rejections
  if (canManage) {
    lineColumns.push({
      title: 'Returns',
      key: 'returns',
      width: 130,
      render: (_: any, record: any) => {
        const rejected = Number(record.rejectedQty);
        const existingReturns = (record.returns || []).reduce((s: number, r: any) => s + Number(r.returnQty), 0);
        const remaining = rejected - existingReturns;

        if (rejected === 0) return <Text type="secondary">-</Text>;

        return (
          <Space direction="vertical" size={0}>
            {existingReturns > 0 && (
              <Text type="secondary" style={{ fontSize: 11 }}>
                {existingReturns} returned
              </Text>
            )}
            {remaining > 0 && (
              <Button
                type="link"
                size="small"
                icon={<RollbackOutlined />}
                onClick={() => openReturnModal(record)}
              >
                Return ({remaining})
              </Button>
            )}
          </Space>
        );
      },
    });
  }

  // Flatten all returns for the returns table
  const allReturns = lines.flatMap((l: any) =>
    (l.returns || []).map((r: any) => ({
      ...r,
      poLineNo: l.poLine?.lineNo,
      itemDescription: l.poLine?.itemDescription,
    }))
  );

  const returnColumns: any[] = [
    {
      title: 'PO Line #',
      dataIndex: 'poLineNo',
      width: 90,
      align: 'center' as const,
    },
    {
      title: 'Item',
      dataIndex: 'itemDescription',
      width: 200,
      ellipsis: true,
    },
    {
      title: 'Return Qty',
      dataIndex: 'returnQty',
      width: 100,
      align: 'right' as const,
      render: (val: any) => Number(val),
    },
    {
      title: 'Reason',
      dataIndex: 'returnReason',
      width: 180,
      ellipsis: true,
    },
    {
      title: 'Date',
      dataIndex: 'returnDate',
      width: 120,
      render: (val: string) => dayjs(val).format('DD MMM YYYY'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 100,
      align: 'center' as const,
      render: (val: string) => <Tag color={RETURN_STATUS_COLORS[val] || 'default'}>{val}</Tag>,
    },
    {
      title: 'Settlement',
      key: 'settlement',
      width: 160,
      render: (_: any, record: any) => {
        if (record.status === 'Credited') return <span><Tag color="green">Credit</Tag>{record.creditNoteRef || ''}</span>;
        if (record.status === 'Refunded') return <span><Tag color="cyan">Refund</Tag>{record.refundRef || ''}{record.refundAmount ? ` (${Number(record.refundAmount).toLocaleString()})` : ''}</span>;
        return <Text type="secondary">-</Text>;
      },
    },
    {
      title: 'By',
      key: 'returnedBy',
      width: 120,
      render: (_: any, record: any) => record.returnedBy?.displayName || '-',
    },
  ];

  if (canManage) {
    returnColumns.push({
      title: 'Action',
      key: 'action',
      width: 140,
      render: (_: any, record: any) => {
        if (record.status === 'Pending') {
          return (
            <Button
              type="link"
              size="small"
              icon={<SendOutlined />}
              onClick={() => handleReturnStatusChange(record.id, 'Shipped')}
            >
              Mark Shipped
            </Button>
          );
        }
        if (record.status === 'Shipped') {
          return (
            <Button
              type="link"
              size="small"
              icon={<DollarOutlined />}
              onClick={() => openSettlementModal(record)}
            >
              Settle
            </Button>
          );
        }
        if (record.status === 'Credited' || record.status === 'Refunded') {
          return <Tag color="green">Complete</Tag>;
        }
        return null;
      },
    });
  }

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => (window.history.state?.idx > 0 ? navigate(-1) : navigate('/grns'))}>Back</Button>
            <Title level={4} style={{ margin: 0 }}>{grn.grnId}</Title>
            <Tag color={qualityPct === 100 ? 'green' : qualityPct >= 80 ? 'orange' : 'red'}>
              {qualityPct}% Quality
            </Tag>
          </Space>
        </Col>
        <Col>
          <Space wrap>
            <Button icon={<PrinterOutlined />} onClick={printSlip}>Print slip</Button>
            {isAdmin && <Button danger icon={<DeleteOutlined />} onClick={handleDeleteGRN}>Delete GRN</Button>}
          </Space>
        </Col>
      </Row>

      <Card style={{ marginBottom: 16 }}>
        <Descriptions bordered size="small" column={{ xs: 1, sm: 2, md: 3 }}>
          <Descriptions.Item label="GRN ID">{grn.grnId}</Descriptions.Item>
          <Descriptions.Item label="PO #">
            <a onClick={() => navigate(`/pos/${grn.poHeader?.id}`)}>{grn.poHeader?.poId || '-'}</a>
          </Descriptions.Item>
          <Descriptions.Item label="Vendor">{grn.poHeader?.vendor?.name || '-'}</Descriptions.Item>
          <Descriptions.Item label="Department">{grn.poHeader?.department?.name || '-'}</Descriptions.Item>
          <Descriptions.Item label="Received On">
            {dayjs(grn.receivedOn).format('DD MMM YYYY')}
            <div style={{ fontSize: 12, color: '#8a94a6' }}>typed in {dayjs(grn.createdAt).format('D MMM YYYY HH:mm')}</div>
            {grn.receivedOnReason && <div style={{ fontSize: 12, color: '#8a94a6' }}>{grn.receivedOnReason}</div>}
          </Descriptions.Item>
          {grn.poHeader?.purchasePath && (
            <Descriptions.Item label="How it was bought">
              <Tag color={grn.poHeader.purchasePath === 'direct' ? 'green' : 'blue'}>{PATH_LABEL[grn.poHeader.purchasePath]}</Tag>
              {grn.poHeader.paymentMethod ? <span>paid by {PAYMENT_LABEL[grn.poHeader.paymentMethod] || grn.poHeader.paymentMethod}</span> : null}
            </Descriptions.Item>
          )}
          {(proofFiles.length > 0 || grn.noProofReason) && (
            <Descriptions.Item label="Proof">
              {proofFiles.map((f: any, i: number) => (
                <div key={f.key}><Button type="link" size="small" icon={<PaperClipOutlined />} style={{ padding: 0 }} onClick={() => openProof(i)}>{f.name}</Button></div>
              ))}
              {grn.noProofReason && <span style={{ color: '#ad6800' }}>No receipt: {grn.noProofReason}</span>}
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Received By">{grn.receivedBy?.displayName || '-'}</Descriptions.Item>
          <Descriptions.Item label="Delivery Note Ref">{grn.deliveryNoteRef || '-'}</Descriptions.Item>
          <Descriptions.Item label="Supplier Invoice">{grn.supplierInvoice || '-'}</Descriptions.Item>
          <Descriptions.Item label="Lines">{grn.lineCount}</Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Summary Stats */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={8}>
          <Card size="small">
            <Statistic title="Received" value={perUnit('acceptedQty')} valueStyle={{ fontSize: 18 }} />
          </Card>
        </Col>
        <Col xs={8}>
          <Card size="small">
            <Statistic title="Value (EGP)" value={money(receiptValue)} valueStyle={{ color: '#389e0d', fontSize: 18 }} prefix={<CheckCircleOutlined />} />
          </Card>
        </Col>
        <Col xs={8}>
          <Card size="small">
            <Statistic
              title="Rejected"
              value={perUnit('rejectedQty')}
              valueStyle={{ color: totalRejected > 0 ? '#ff4d4f' : undefined, fontSize: 18 }}
              prefix={totalRejected > 0 ? <CloseCircleOutlined /> : undefined}
            />
          </Card>
        </Col>
      </Row>

      {hasRejections && (
        <Alert
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          message={`${totalRejected} items rejected in this delivery`}
          description="Use the Returns section below to track return shipments to the vendor."
          style={{ marginBottom: 16 }}
        />
      )}

      {grn.notes && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Text strong>Notes: </Text>
          <Text>{grn.notes}</Text>
        </Card>
      )}

      <Card title="GRN Lines" style={{ marginBottom: 16 }}>
        <ResponsiveTable
          card={{ header: ['partNumber', 'description'] }}
          columns={lineColumns}
          dataSource={lines}
          rowKey="id"
          pagination={false}
          size="small"
          scroll={{ x: 1246 }}
        />
      </Card>

      {allReturns.length > 0 && (
        <Card title="Returns" style={{ marginBottom: 16 }}>
          <ResponsiveTable
            columns={returnColumns}
            dataSource={allReturns}
            rowKey="id"
            pagination={false}
            size="small"
            scroll={{ x: 1000 }}
          />
        </Card>
      )}

      {grn && (
        <Card style={{ marginTop: 16 }}>
          <EntityHistory entityType="GRN" entityId={grn.grnId} />
        </Card>
      )}

      {/* Return Modal */}
      <Modal
        title="Create Return for Rejected Items"
        open={returnModalOpen}
        onOk={handleCreateReturn}
        onCancel={() => setReturnModalOpen(false)}
        confirmLoading={returnLoading}
        okText="Create Return"
      >
        {returningLine && (
          <div>
            <Descriptions size="small" bordered column={1} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Item">{returningLine.poLine?.itemDescription}</Descriptions.Item>
              <Descriptions.Item label="Rejected Qty">{Number(returningLine.rejectedQty)}</Descriptions.Item>
              <Descriptions.Item label="Available to Return">{returningLine.maxReturn}</Descriptions.Item>
            </Descriptions>

            <div style={{ marginBottom: 12 }}>
              <Text strong>Return Quantity:</Text>
              <InputNumber
                min={1}
                max={returningLine.maxReturn}
                value={returnForm.returnQty}
                onChange={(v) => setReturnForm((f) => ({ ...f, returnQty: v || 0 }))}
                style={{ width: '100%', marginTop: 4 }}
              />
            </div>

            <div style={{ marginBottom: 12 }}>
              <Text strong>Return Reason: <Text type="danger">*</Text></Text>
              <Input.TextArea
                rows={2}
                value={returnForm.returnReason}
                onChange={(e) => setReturnForm((f) => ({ ...f, returnReason: e.target.value }))}
                placeholder="e.g., Damaged fabric - wrong color shade"
                style={{ marginTop: 4 }}
              />
            </div>

            <div>
              <Text strong>Notes (optional):</Text>
              <Input.TextArea
                rows={2}
                value={returnForm.notes}
                onChange={(e) => setReturnForm((f) => ({ ...f, notes: e.target.value }))}
                placeholder="Additional notes..."
                style={{ marginTop: 4 }}
              />
            </div>
          </div>
        )}
      </Modal>

      {/* Settlement Modal */}
      <Modal
        title="Settle Return"
        open={settlementModalOpen}
        onOk={handleSettlement}
        onCancel={() => setSettlementModalOpen(false)}
        okText={settlementForm.type === 'Credit' ? 'Mark as Credited' : 'Mark as Refunded'}
      >
        {settlingReturn && (
          <div>
            <Descriptions size="small" bordered column={1} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Return Qty">{Number(settlingReturn.returnQty)}</Descriptions.Item>
              <Descriptions.Item label="Reason">{settlingReturn.returnReason}</Descriptions.Item>
            </Descriptions>

            <div style={{ marginBottom: 16 }}>
              <Text strong>Settlement Type: <Text type="danger">*</Text></Text>
              <Select
                style={{ width: '100%', marginTop: 4 }}
                value={settlementForm.type}
                onChange={(v) => setSettlementForm((f) => ({ ...f, type: v }))}
                options={[
                  { label: 'Credit Note — Vendor issues credit for future orders', value: 'Credit' },
                  { label: 'Refund — Vendor returns money', value: 'Refund' },
                ]}
              />
            </div>

            {settlementForm.type === 'Credit' && (
              <div style={{ marginBottom: 12 }}>
                <Text strong>Credit Note Reference:</Text>
                <Input
                  value={settlementForm.creditNoteRef}
                  onChange={(e) => setSettlementForm((f) => ({ ...f, creditNoteRef: e.target.value }))}
                  placeholder="e.g., CN-2026-0042"
                  style={{ marginTop: 4 }}
                />
              </div>
            )}

            {settlementForm.type === 'Refund' && (
              <>
                <div style={{ marginBottom: 12 }}>
                  <Text strong>Refund Reference:</Text>
                  <Input
                    value={settlementForm.refundRef}
                    onChange={(e) => setSettlementForm((f) => ({ ...f, refundRef: e.target.value }))}
                    placeholder="e.g., Bank transfer ref, cheque number"
                    style={{ marginTop: 4 }}
                  />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <Text strong>Refund Amount:</Text>
                  <InputNumber
                    min={0}
                    value={settlementForm.refundAmount}
                    onChange={(v) => setSettlementForm((f) => ({ ...f, refundAmount: v || 0 }))}
                    style={{ width: '100%', marginTop: 4 }}
                    formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  />
                </div>
              </>
            )}
          </div>
        )}
      </Modal>

    </div>
  );
};

export default GRNView;
